<script lang="ts">
    import { onMount, createEventDispatcher } from 'svelte';

    import { api } from '../js/api.js';
    import { auth } from '../js/auth.js';
    import type { ApiPaging, ApiResponse } from '../js/apiTypes.js';
    import { getNestedProperty } from '../js/utils.js';
    import {
        emptyTableData,
        getSelectOptions,
        optionProp,
        type IndexParams,
        type ListRecord,
        type TableData,
    } from '../js/tableTypes.js';

    import ModelView from './ModelView.svelte';
    import ModalConfirm from './ModalConfirm.svelte';
    import Card from './Card.svelte';

    export let displayViewer = false;
    export let modelName = '';
    export let enableSearch = true;
    export let tableData: TableData = emptyTableData;

    let list: ListRecord[] = [];
    let paging: ApiPaging = { current_page: 0, last_page: 0 };
    let currentId: string | null = '';
    let currentPage = 1;
    let sortColumn = '';
    let sortDirection: 'asc' | 'desc' = 'asc';
    let searchPhrase = '';
    let searchOptions = new Map<string, string>();
    let displayConfirm = false;

    const dispatch = createEventDispatcher<{
        [key: string]: { item: ListRecord; srcElement: EventTarget | null };
    }>();

    const createRecord = () => {
        showModel(null);
    };

    const setSearchPhrase = (phrase: string) => {
        searchPhrase = phrase;
        refreshTable();
    };

    const showModel = (id: string | null) => {
        currentId = id;
        displayViewer = true;
    };

    const deleteConfirm = (id: string) => {
        currentId = id;
        displayConfirm = true;
    };

    const deleteRecord = async () => {
        displayConfirm = false;
        if (currentId) {
            await api.crudDelete(modelName, currentId);
        }
        refreshTable();
    };

    const getIndex = async (parameters: IndexParams = {}) => {
        const response = await api.crudIndex(modelName, parameters) as ApiResponse<ListRecord[]>;
        list = response.data ?? [];
        paging = response.paging ?? { current_page: 0, last_page: 0 };
    };

    const refreshTable = () => {
        const searchOptionsObject = Object.fromEntries(searchOptions);
        getIndex({
            page: currentPage,
            phrase: searchPhrase,
            column: sortColumn,
            order: sortDirection,
            ...searchOptionsObject,
        });
    };

    const setCurrentPage = (page: number) => {
        currentPage = page;
        refreshTable();
    };

    onMount(async () => {
        if (tableData.details.sortby) {
            sortColumn = tableData.details.sortby;
        } else if (tableData.fields.length > 0) {
            sortColumn = tableData.fields[0].key;
        }
        setCurrentPage(1);
    });

    const sortTable = (column: string) => {
        if (sortColumn === column) {
            sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            sortColumn = column;
            sortDirection = 'asc';
        }
        refreshTable();
    };

    const setSearchOption = (searchKey: string, value: string) => {
        if (value === '') {
            searchOptions.delete(searchKey);
        } else {
            searchOptions.set(searchKey, value);
        }
        refreshTable();
    };

    function handleSearchKeydown(e: KeyboardEvent & { currentTarget: HTMLInputElement }) {
        if (e.key === 'Enter') {
            e.preventDefault();
            setSearchPhrase(e.currentTarget.value);
            refreshTable();
        }
    }

    function handleSelectChange(
        e: Event & { currentTarget: HTMLSelectElement },
        searchKey: string,
    ) {
        setSearchOption(searchKey, e.currentTarget.value);
    }
</script>

{#if $auth.token}

    <h2 class="ui header">{tableData.details.titleList}</h2>
    {#if enableSearch == true}
    <div class="ui icon input">
        <i class="search icon"></i>
        {#each tableData.fields as field (field.key)}
        {#if field.type === 'select' && field.select?.searchable}
        <select on:change={(e) => handleSelectChange(e, field.select?.search_key ?? field.key)}>
            <option selected value=''>{`Any ${field.label}`}</option>
            {#each getSelectOptions($api.lists, field.select.data_list) as option (optionProp(option, field.select.key))}
            <option value={optionProp(option, field.select.key)}>{optionProp(option, field.select.display)}</option>
            {/each}
        </select>
        {/if}
        {/each}
        <input type="text" placeholder="Search..."
            on:keydown={handleSearchKeydown}>
    </div>
    {/if}
    {#if tableData.details.create}
    <button on:click={createRecord} class="ui button">New Record</button>
    {/if}
    <div class="ui hidden divider"></div>
    <div class="ui six doubling cards">
        {#each list as item (item.uuid)}
            <Card actions={tableData.actions != null && tableData.actions.length > 0}>
                <span slot="content">
                    {#each tableData.fields as field, i}
                        {#if field.key === 'media_file' && item.media_file && item.media_file.url}
                            <div class="card-list-data">
                                <img src={item.media_file.url} alt={`Image for ${item.media_file.url}`} class="ui image medium centered" />
                            </div>
                        {:else}
                            {#if i > 0}
                                {#if i === 1}
                                    <h3 class="ui sub header">{getNestedProperty(item, field.key)}</h3>
                                {:else}
                                    <h3 class="buytoken{i}">${getNestedProperty(item, field.key)}</h3>
                                {/if}
                            {/if}
                        {/if}
                    {/each}
                </span>

                <span slot="actions">
                    {#each tableData.actions ?? [] as action}
                        <button type="button" on:click={(event) => {
                            dispatch(action.action, { item, srcElement: event.currentTarget })
                        }} class="{action.class}">
                            {action.label}
                        </button>
                    {/each}
                </span>
            </Card>
        {/each}

    </div>

    <div class="ui hidden divider"></div>
{#if (paging.last_page ?? 0) > 1}
    <div class="ui pagination menu">
        {#if (paging.current_page ?? 0) > 0}
            <button type="button" on:click={() => setCurrentPage(0)} class="item" aria-label="First page"><i class="angle left icon" aria-hidden="true"></i></button>
            <button type="button" on:click={() => setCurrentPage(paging.current_page ?? 0)} class="item" aria-label="Previous page"><i class="angle double left icon" aria-hidden="true"></i></button>
        {/if}
        {#each Array.from({length: paging.last_page ?? 0}).map((_, i) => i) as page (page)}
            {#if page >= (paging.current_page ?? 0) - 5 && page <= (paging.current_page ?? 0) + 5}
                <button type="button" on:click={() => setCurrentPage(page + 1)}
                        class={page === paging.current_page ? 'active item' : 'item'}>
                    {page + 1}
                </button>
            {/if}
        {/each}
        {#if (paging.current_page ?? 0) < (paging.last_page ?? 0) - 1}
            <button type="button" on:click={() => setCurrentPage((paging.current_page ?? 0) + 2)} class="item" aria-label="Next page"><i class="angle right icon" aria-hidden="true"></i></button>
            <button type="button" on:click={() => setCurrentPage(paging.last_page ?? 0)} class="item" aria-label="Last page"><i class="angle double right icon" aria-hidden="true"></i></button>
        {/if}
    </div>
{/if}

<ModelView
    on:saved={() => setCurrentPage(currentPage)}
    bind:modelName={modelName}
    bind:modelId={currentId}
    bind:displayModal={displayViewer}
    {tableData}
/>

<ModalConfirm on:yes="{deleteRecord}" bind:showModal={displayConfirm}>
    <div slot="body">
        Are you sure you want to delete this record?
    </div>
</ModalConfirm>

{:else}
<p>You must be logged in to view this page.</p>
{/if}
